import { FlexPreset } from "./types";

const parsePresetZoneList = (data: unknown): Array<{ id?: unknown }> => {
  if (data && typeof data === "object" && Array.isArray((data as { results?: unknown }).results)) {
    return (data as { results: Array<{ id?: unknown }> }).results;
  }
  if (Array.isArray(data)) {
    return data as Array<{ id?: unknown }>;
  }
  return [];
};

export const collectPersistedPresetIdsForCameras = (
  presetsByCamera: Record<number, FlexPreset[]>,
  cameraIds: number[]
): number[] => {
  const ids = new Set<number>();
  cameraIds.forEach((cameraId) => {
    (presetsByCamera[cameraId] || []).forEach((preset) => {
      if (preset.persistedId != null && Number.isFinite(Number(preset.persistedId))) {
        ids.add(Number(preset.persistedId));
      }
    });
  });
  return Array.from(ids);
};

export const collectPersistedPresetZoneIdsForCameras = (
  presetsByCamera: Record<number, FlexPreset[]>,
  cameraIds: number[]
): number[] => {
  const ids = new Set<number>();
  cameraIds.forEach((cameraId) => {
    (presetsByCamera[cameraId] || []).forEach((preset) => {
      if (preset.persistedId == null) {
        return;
      }
      (preset.zones || []).forEach((zone) => {
        if (typeof zone.preset_zone_id === "number" && Number.isFinite(zone.preset_zone_id)) {
          ids.add(zone.preset_zone_id);
        }
      });
    });
  });
  return Array.from(ids);
};

export type FlexOffboardingCleanupApi = {
  get: (url: string) => Promise<{ data: unknown }>;
  delete: (url: string) => Promise<unknown>;
};

/**
 * After standard ignore_zones POSTs, remove flex preset-zone rows and presets from the API,
 * then refresh local camera context. Preset-zones are deleted before presets.
 */
export async function deletePersistedFlexPresetRecordsAfterIgnoreZonesSave(
  AxiosUI: FlexOffboardingCleanupApi,
  presetsByCamera: Record<number, FlexPreset[]>,
  cameras: { id: number }[],
  loadCameraContext: (cameraId: number, forceRefresh?: boolean) => Promise<void>
): Promise<void> {
  const cameraIds = cameras.map((c) => c.id);
  let presetZoneIds = collectPersistedPresetZoneIdsForCameras(presetsByCamera, cameraIds);
  const presetIds = collectPersistedPresetIdsForCameras(presetsByCamera, cameraIds);

  if (presetZoneIds.length === 0 && presetIds.length > 0) {
    const fetched = new Set<number>();
    for (const camera of cameras) {
      const camPresets = presetsByCamera[camera.id] || [];
      for (const preset of camPresets) {
        if (preset.persistedId == null) {
          continue;
        }
        const pzRes = await AxiosUI.get(
          `preset-zone/?camera=${camera.id}&preset=${preset.persistedId}`
        );
        parsePresetZoneList(pzRes.data).forEach((row) => {
          if (row.id != null && Number.isFinite(Number(row.id))) {
            fetched.add(Number(row.id));
          }
        });
      }
    }
    presetZoneIds = Array.from(fetched);
  }

  for (const zoneId of presetZoneIds) {
    await AxiosUI.delete(`preset-zone/${zoneId}/`);
  }
  for (const presetId of presetIds) {
    await AxiosUI.delete(`preset/${presetId}/`);
  }

  await Promise.all(cameras.map((camera) => loadCameraContext(camera.id, true)));
}
